import React, { useEffect } from 'react'
import styled from '@emotion/styled'

import { Box } from '../Box/Box'
import { Text } from '../Text/Text'

export interface SliderProps {
  value: number
  min: number
  max: number
  step?: number | string
  onChange: (newValue: number) => void
  tone?: 'action' | 'positive' | 'caution' | 'negative'
  tooltip?: boolean
}

export const Slider: React.FC<SliderProps> = ({
  value = 50,
  min = 0,
  max = 100,
  step = '',
  onChange,
  tooltip = true,
  tone = 'action'
}) => {
  useEffect(() => {
    if (value < min) onChange(min)
    if (value > max) onChange(max)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const getValueDistance = () => {
    let valueDistance = ((value - min) / (max - min)) * 100
    return Math.round(valueDistance)
  }

  return (
    <Box className="slider--wrapper" position="relative" height={tooltip ? '80px' : '20px'}>
      <StyledInput
        className="slider--input"
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(parseInt(e.target.value))}
        tone={tone}
        thumbPosition={getValueDistance()}
      />
      <Box
        className="slider--filled-track"
        background={`var(--colors-${tone})`}
        height={6}
        width={getValueDistance() + '%'}
        radius="l"
        position="absolute"
        top="50%"
        transform="translateY(-50%)"
        pointerEvents="none"
      />
      <Box
        className="slider--thumb"
        background={`var(--colors-${tone})`}
        position="absolute"
        height={20}
        width={20}
        radius="circle"
        top="50%"
        transform="translateY(-50%)"
        left={`calc(${getValueDistance()}% - 10px)`}
        pointerEvents="none"
      ></Box>
      {tooltip && (
        <Box
          className="slider--tooltip"
          position="absolute"
          width={40}
          height={30}
          top={0}
          display="flex"
          justifyContent="center"
          alignItems="bottom"
          paddingBottom="xs"
          left={`calc(${getValueDistance()}% - 20px)`}
        >
          <Text size="bigger" weight="stronger" align="center">
            {value}
          </Text>
        </Box>
      )}
    </Box>
  )
}

interface StyledInputProps {
  tone: 'action' | 'positive' | 'caution' | 'negative'
  thumbPosition: number
}

const StyledInput = styled.input<StyledInputProps>`
  appearance: none;
  width: 100%;
  background: transparent;
  color: transparent;
  box-shadow: none;
  border-color: transparent;
  border-radius: var(--border-radius--l);
  height: 20px;
  cursor: pointer;
  margin: 0;
  top: 50%;
  position: absolute;
  transform: translateY(-50%);

  &:focus {
    outline: none;
  }

  &::-webkit-slider-runnable-track {
    height: 4px;
    width: 100%;
    background: var(--colors-${(p) => p.tone}--subtle);
    border-color: transparent;
    color: transparent;
  }

  &::-moz-range-track {
    height: 4px;
    width: 100%;
    background: var(--colors-${(p) => p.tone}--subtle);
    border-color: transparent;
    color: transparent;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    height: 30px;
    width: 40px;
    margin-top: -14px;
    opacity: 0;
    ${(p) =>
      p.thumbPosition < 50 &&
      `
      transform: translateX(-10px);
    `}
    ${(p) =>
      p.thumbPosition > 50 &&
      `
      transform: translateX(10px);
    `}
    // opacity: 0;;;
  }

  &::-moz-range-thumb {
    -webkit-appearance: none;
    opacity: 0;
    background: red;
    height: 30px;
    width: 40px;

    ${(p) =>
      p.thumbPosition < 50 &&
      `
      transform: translateX(-10px);
    `}
    ${(p) =>
      p.thumbPosition > 50 &&
      `
      transform: translateX(10px);
    `}
  }
`
